import React from 'react';
import { ValidatorData } from '../../types/api';

interface ValidatorMetricsProps {
  validators: ValidatorData[];
}

const ValidatorMetrics: React.FC<ValidatorMetricsProps> = ({ validators }) => {
  const onlineCount = validators.filter(v => v.isOnline).length;
  const offlineCount = validators.length - onlineCount;
  
  const averageSuccessRate = validators.length > 0 
    ? validators.reduce((sum, v) => sum + (v.successRate || 0), 0) / validators.length 
    : 0;
    
  const averageResponseTime = validators.length > 0 
    ? validators.reduce((sum, v) => sum + (v.responseTime || 0), 0) / validators.length 
    : 0;

  const totalApprovals = validators.reduce((sum, v) => sum + (v.totalApprovals || 0), 0);

  const highPerformanceCount = validators.filter(v => (v.successRate || 0) >= 95).length;
  const lowPerformanceCount = validators.filter(v => (v.successRate || 0) < 90).length;

  const formatResponseTime = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const metrics = [
    {
      title: 'Total Validators',
      value: validators.length.toString(),
      icon: '👥',
      color: 'blue'
    },
    {
      title: 'Online Validators',
      value: `${onlineCount}/${validators.length}`,
      subtitle: `${((onlineCount / Math.max(validators.length, 1)) * 100).toFixed(1)}%`,
      icon: '🟢',
      color: onlineCount === validators.length ? 'green' : offlineCount > 0 ? 'red' : 'yellow'
    },
    {
      title: 'Average Success Rate',
      value: `${averageSuccessRate.toFixed(1)}%`,
      icon: '✅',
      color: averageSuccessRate >= 95 ? 'green' : averageSuccessRate >= 90 ? 'yellow' : 'red'
    },
    {
      title: 'Average Response Time',
      value: formatResponseTime(averageResponseTime),
      icon: '⚡',
      color: averageResponseTime < 1000 ? 'green' : averageResponseTime < 3000 ? 'yellow' : 'red'
    },
    {
      title: 'Total Approvals',
      value: totalApprovals.toLocaleString(),
      icon: '📝',
      color: 'purple'
    },
    {
      title: 'High Performance',
      value: `${highPerformanceCount}/${validators.length}`,
      subtitle: '≥95% success rate',
      icon: '🚀',
      color: 'green'
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return 'text-blue-600 bg-blue-100';
      case 'green':
        return 'text-green-600 bg-green-100';
      case 'yellow':
        return 'text-yellow-600 bg-yellow-100';
      case 'red':
        return 'text-red-600 bg-red-100';
      case 'purple':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">Validator Network Overview</h3>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {metrics.map((metric, index) => (
          <div key={index} className="flex items-center p-4 bg-gray-50 rounded-lg">
            <div className={`flex-shrink-0 rounded-md p-3 ${getColorClasses(metric.color)}`}>
              <span className="text-2xl">{metric.icon}</span>
            </div>
            <div className="ml-4 flex-1">
              <h4 className="text-sm font-medium text-gray-900">{metric.title}</h4>
              <p className="text-xl font-semibold text-gray-700">{metric.value}</p>
              {metric.subtitle && (
                <p className="text-xs text-gray-500">{metric.subtitle}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Health Status Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Network Health</span>
          <span className="text-sm text-gray-500">
            {onlineCount} online, {offlineCount} offline
          </span>
        </div>
        <div className="flex w-full bg-gray-200 rounded-full h-3">
          {validators.length > 0 && (
            <>
              <div 
                className="bg-green-500 h-3 rounded-l-full"
                style={{ width: `${(onlineCount / validators.length) * 100}%` }}
              ></div>
              {offlineCount > 0 && (
                <div 
                  className="bg-red-500 h-3 rounded-r-full"
                  style={{ width: `${(offlineCount / validators.length) * 100}%` }}
                ></div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Performance Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-600">{highPerformanceCount}</div>
          <div className="text-sm text-green-700">High Performance</div>
          <div className="text-xs text-green-600">≥95% success</div>
        </div>
        
        <div className="text-center p-4 bg-yellow-50 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">
            {validators.filter(v => v.successRate >= 90 && v.successRate < 95).length}
          </div>
          <div className="text-sm text-yellow-700">Good Performance</div>
          <div className="text-xs text-yellow-600">90-95% success</div>
        </div>
        
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <div className="text-2xl font-bold text-red-600">{lowPerformanceCount}</div>
          <div className="text-sm text-red-700">Needs Attention</div>
          <div className="text-xs text-red-600">&lt;90% success</div>
        </div>
      </div>

      {/* Alerts */}
      {offlineCount > 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-red-800">
              <span className="font-medium">{offlineCount}</span> validator(s) are currently offline. 
              Bridge security may be compromised.
            </p>
          </div>
        </div>
      )}

      {lowPerformanceCount > 0 && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-yellow-800">
              <span className="font-medium">{lowPerformanceCount}</span> validator(s) have low success rates 
              and may need attention.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidatorMetrics; 